<template>
  <div class="export-proof-edit-filter">
    <h3 class="export-proof-edit-filter__h3">
      Default Date *
    </h3>
    <div class="export-proof-edit-filter__date">
      <div :class="{'export-proof-edit-filter__date--active': selectedDefaultDate.type === 'range'}" style="grid-column: 1">
        <h4 class="export-proof-edit-filter__h3">
          Date Range
        </h4>
        <form-label>from (days back in time)</form-label>
        <form-input
          :value="getDateValue('range', 'from')"
          type="number"
          data-cy="edit-export-proof-date-days-from"
          :min="0"
          @input="handleDateInput($event, 'range', 'from')" />
        <form-label>to (days back in time)</form-label>
        <form-input
          :value="getDateValue('range', 'to')"
          type="number"
          data-cy="edit-export-proof-date-days-to"
          :min="0"
          @input="handleDateInput($event, 'range', 'to')" />
      </div>
      <div :class="{'export-proof-edit-filter__date--active': selectedDefaultDate.type === 'weeks'}" style="grid-column: 2">
        <h4 class="export-proof-edit-filter__h3">
          Last number of weeks
        </h4>
        <form-label>Number of weeks back (Mon - Sun)</form-label>
        <form-input
          :value="getDateValue('weeks')"
          type="number"
          data-cy="edit-export-proof-date-weeks"
          :min="1"
          @input="handleDateInput($event, 'weeks')" />
      </div>
      <div :class="{'export-proof-edit-filter__date--active': selectedDefaultDate.type === 'months'}" style="grid-column: 3">
        <h4 class="export-proof-edit-filter__h3">
          Last number of months
        </h4>
        <form-label>Number of months back</form-label>
        <form-input
          :value="getDateValue('months')"
          type="number"
          data-cy="edit-export-proof-date-months"
          :min="1"
          @input="handleDateInput($event, 'months')" />
      </div>
      <div :class="{'export-proof-edit-filter__date--active': selectedDefaultDate.type === 'years'}" style="grid-column: 4">
        <h4 class="export-proof-edit-filter__h3">
          Last number of years
        </h4>
        <form-label>Number of years back (Jan - Dec)</form-label>
        <form-input
          :value="getDateValue('years')"
          type="number"
          data-cy="edit-export-proof-date-years"
          :min="1"
          @input="handleDateInput($event, 'years')" />
      </div>
    </div>
    <h3 class="export-proof-edit-filter__h3">
      Default Fields
    </h3>
    <div class="export-proof-edit-filter__fields">
      <div style="grid-column: 1; grid-row: 1;">
        <form-label>Country</form-label>
        <country-select
          v-model="selectedCountry"
          placeholder="Countries"
          data-cy="edit-export-proof-countries"
          :invalid="$v.selectedCountry.$error"
          @close="$v.selectedCountry.$touch()" /><br>
        <span v-if="$v.selectedCountry.$error" class="error">Country is required.</span><br>
      </div>
      <div style="grid-column: 2; grid-row: 1;">
        <form-label>Customer</form-label>
        <customer-select
          v-model="selectedCustomer"
          placeholder="Customers"
          data-cy="edit-export-proof-customers"
          :invalid="$v.selectedCustomer.$error"
          @close="$v.selectedCustomer.$touch()" /><br>
        <span v-if="$v.selectedCustomer.$error" class="error">Customer is required.</span><br>
      </div>
      <div style="grid-column: 1; grid-row: 2;">
        <form-label>Parcel Status</form-label>
        <form-filtered-select
          v-model="defaultFilter.Parcel_Status"
          :options="constants.PARCEL_STATUS_EXPORT_PROOF_OPTIONS"
          :selected-key="constants.PARCEL_STATUS.DELIVERED"
          track-by="value"
          label="label"
          data-cy="edit-export-proof-parcel-status"
          placeholder="Parcel Status" />
      </div>
      <div style="grid-column: 2; grid-row: 2;">
        <form-label>Service Level</form-label>
        <form-filtered-multiselect
          v-model="defaultFilter.Service_Levels"
          :options="serviceLevelOptions"
          data-cy="edit-export-proof-service-level"
          track-by="value"
          label="label"
          placeholder="Service Level" />
      </div>
      <div style="grid-column: 1; grid-row: 3;">
        <form-label>Current Event</form-label>
        <form-filtered-multiselect
          v-model="defaultFilter.Current_Event_Infos"
          :options="exportProofEventInfoOptions"
          track-by="value"
          label="label"
          data-cy="edit-export-proof-current-event"
          placeholder="Current Event" />
      </div>
    </div>
    <h3 class="export-proof-edit-filter__h3">
      Default Postal Code
    </h3>
    <div class="export-proof-edit-filter__postal">
      <div>
        <form-label>Postal Code From</form-label>
        <form-input v-model="defaultFilter.From_Postal_Code" data-cy="edit-export-proof-postal-code-from" />
      </div>
      <div>
        <form-label>Postal Code To</form-label>
        <form-input v-model="defaultFilter.To_Postal_Code" data-cy="edit-export-proof-postal-code-to" />
      </div>
    </div>
    <h3 class="export-proof-edit-filter__h3">
      Default References
    </h3>
    <div class="export-proof-edit-filter__references">
      <div style="grid-column: 1; grid-row: 1;">
        <form-label>MRN Numbers</form-label>
        <form-input
          v-model="defaultFilter.MRN_Number"
          placeholder="MRN Numbers divide by ,"
          data-cy="edit-export-proof-mrn-numbers" />
      </div>
      <div style="grid-column: 2; grid-row: 1;">
        <form-label>Counter Numbers</form-label>
        <form-input
          v-model="defaultFilter.Counter_Number"
          placeholder="Counter Numbers divide by ,"
          data-cy="edit-export-proof-counter-numbers" />
      </div>
      <div style="grid-column: 1; grid-row: 2;">
        <form-label>Delivery Note Numbers</form-label>
        <form-input
          v-model="defaultFilter.Delivery_Note_Number"
          placeholder="Delivery Note Numbers divide by ,"
          data-cy="edit-export-proof-delivery-note-numbers" />
      </div>
    </div>
    <h3 class="export-proof-edit-filter__h3">
      Email recipient list
    </h3>
    <div class="export-proof-edit-filter__mail">
      <div>
        <form-label>To recipients</form-label>
        <form-input
          v-model="defaultFilter.To_Mail_List"
          placeholder="Emails to divide by ;"
          data-cy="edit-export-proof-to-mail" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import constants from '@/constants'
import { set, cloneDeep } from 'lodash'
import { mapGetters, mapMutations, mapState } from 'vuex'
import { ExportProof } from '@/webApi/DataModels'
import { required } from 'vuelidate/lib/validators'

export default {
  data () {
    return {
      selectedDefaultDate: {},
      defaultFilter: {
        Countries: [],
        Current_Event_Infos: [],
        Customers: [],
        Service_Levels: [],
        Parcel_Status: [],
        Show_Forward_Flow: true,
        From_Postal_Code: '',
        To_Postal_Code: '',
        Delivery_Note_Number: '',
        MRN_Number: '',
        Counter_Number: '',
        To_Mail_List: ''
      },
      constants,
      selectedCountry: {},
      selectedCustomer: {}
    }
  },
  validations: {
    selectedCountry: {
      required
    },
    selectedCustomer: {
      required
    }
  },
  computed: {
    ...mapGetters('serviceLevel', ['serviceLevelOptions']),
    ...mapGetters('country', ['countryOptions']),
    ...mapGetters('customer', ['customerOptions']),
    ...mapState('eventInfo', ['exportProofEventInfoOptions']),
    ...mapState('exportProof', ['currentEditExportProof'])
  },
  watch: {
    selectedCountry: function () {
      if (this.selectedCountry) {
        this.defaultFilter.Countries = [this.selectedCountry.value]
        this.setCurrentEditExportProof(set(cloneDeep(this.currentEditExportProof), 'Export_Proof_Filter', this.defaultFilter))
      }
    },
    selectedCustomer: function () {
      if (this.selectedCustomer) {
        this.defaultFilter.Customers = [this.selectedCustomer.value]
        this.setCurrentEditExportProof(set(cloneDeep(this.currentEditExportProof), 'Export_Proof_Filter', this.defaultFilter))
      }
    },
    defaultFilter: {
      handler: function () {
        // update the export proof filter everytime the parcel filter changes
        this.setCurrentEditExportProof(set(cloneDeep(this.currentEditExportProof), 'Export_Proof_Filter', this.defaultFilter))
      },
      deep: true
    }
  },
  mounted () {
    // get the values out of the current export proof (when editing for example) and transform them into a usable format
    this.defaultFilter = cloneDeep(this.currentEditExportProof.Export_Proof_Filter)
    if (!this.defaultFilter.Parcel_Status) {
      this.defaultFilter.Parcel_Status = []
    }
    if (this.defaultFilter.Parcel_Status.length === 0) {
      this.defaultFilter.Parcel_Status.push(constants.PARCEL_STATUS.DELIVERED)
    }
    if (!this.defaultFilter.Countries) {
      this.defaultFilter.Countries = []
    }
    if (!this.defaultFilter.Customers) {
      this.defaultFilter.Customers = []
    }
    if (this.currentEditExportProof.Export_Proof_Filter.Countries) {
      this.set_Country_Dropdown_By_ID(this.currentEditExportProof.Export_Proof_Filter.Countries[0])
    }
    if (this.currentEditExportProof.Export_Proof_Filter.Customers) {
      this.set_Customer_Dropdown_By_ID(this.currentEditExportProof.Export_Proof_Filter.Customers[0])
    }
    if (this.currentEditExportProof.Date_Range_From !== -1) {
      this.selectedDefaultDate = {
        type: 'range',
        from: this.currentEditExportProof.Date_Range_From,
        to: this.currentEditExportProof.Date_Range_To
      }
    } else if (this.currentEditExportProof.Last_Months !== -1) {
      this.selectedDefaultDate = {
        type: 'months',
        value: this.currentEditExportProof.Last_Months
      }
    } else if (this.currentEditExportProof.Last_Weeks !== -1) {
      this.selectedDefaultDate = {
        type: 'weeks',
        value: this.currentEditExportProof.Last_Weeks
      }
    } else if (this.currentEditExportProof.Last_Years !== -1) {
      this.selectedDefaultDate = {
        type: 'years',
        value: this.currentEditExportProof.Last_Years
      }
    }
  },
  methods: {
    ...mapMutations('exportProof', ['setCurrentEditExportProof']),
    onCountriesSelected (countries) {
      this.defaultFilter.Countries = countries
    },
    set_Country_Dropdown_By_ID (countryID) {
      for (var i in this.countryOptions) {
        if (this.countryOptions[i].value === countryID) {
          this.selectedCountry = this.countryOptions[i]
          return
        }
      }
    },
    set_Customer_Dropdown_By_ID (customerID) {
      for (var i in this.customerOptions) {
        if (this.customerOptions[i].value === customerID) {
          this.selectedCustomer = this.customerOptions[i]
          return
        }
      }
    },
    onCustomersSelected (customers) {
      this.defaultFilter.Customers = customers
    },
    onStatusSelected (parcelStataus) {
      this.defaultFilter.Parcel_Status = parcelStataus
    },
    onServiceLevelSelected (serviceLevel) {
      this.defaultFilter.Service_Levels = serviceLevel
    },
    onEventSelected (event) {
      this.defaultFilter.Current_Event_Infos = event
    },
    // Handles input changes in the date fiels,
    // only one selection is possible so it
    // resets the selected date object if another
    // type is selected
    handleDateInput (value, type, specifier) {
      if (value === null) {
        return
      }
      if (this.selectedDefaultDate.type !== type) {
        this.selectedDefaultDate = {}
      }
      this.selectedDefaultDate.type = type
      if (specifier !== undefined) {
        this.selectedDefaultDate[specifier] = value
      } else {
        this.selectedDefaultDate.value = value
      }
      this.writeDateFilter()
      this.$forceUpdate()
    },
    // Gets the selected date value, in order to render the right value from the object that is selected
    getDateValue (type, specifier) {
      if (this.selectedDefaultDate.type === type) {
        if (specifier) {
          return this.selectedDefaultDate[specifier]
        } else {
          return this.selectedDefaultDate.value
        }
      }
      return null
    },
    writeDateFilter () {
      // Everytime the date filter is changed write it into a usable format for storing
      var exportProof:ExportProof = cloneDeep(this.currentEditExportProof) as ExportProof
      exportProof.Date_Range_From = -1
      exportProof.Date_Range_To = -1
      exportProof.Last_Months = -1
      exportProof.Last_Weeks = -1
      exportProof.Last_Years = -1
      switch (this.selectedDefaultDate.type) {
        case 'range':
          exportProof.Date_Range_From = parseInt(this.selectedDefaultDate.from)
          exportProof.Date_Range_To = parseInt(this.selectedDefaultDate.to)
          break
        case 'weeks':
          exportProof.Last_Weeks = parseInt(this.selectedDefaultDate.value)
          break
        case 'months':
          exportProof.Last_Months = parseInt(this.selectedDefaultDate.value)
          break
        case 'years':
          exportProof.Last_Years = parseInt(this.selectedDefaultDate.value)
          break
      }
      this.setCurrentEditExportProof(exportProof)
    }
  }
}
</script>

<style lang="scss">
  @import '../../../variables.scss';
  .export-proof-edit-filter {
    margin-bottom: 100px;
    &__date {
      display: grid;
      grid-template-columns: 24% 24% 24% 24%;
      grid-gap: 7px;
      width: 100%;

      &--active {
        background-color: $grey_80 !important;
      }

      div {
        background-color: $grey_95;
        border-radius: 5px;
        padding: 10px;
      }

      .spl-checkbox-group__item__label {
        margin-bottom: 10px;
      }
    }
    &__h3 {
        padding-top: 10px !important;
        padding-bottom: 10px !important;
    }
    &__fields, &__postal, &__references, &__mail {
       display: grid;
      grid-template-columns: 49% 49%;
      grid-gap: 7px;
      width: 100%;
      div {
        margin-bottom: 10px;
      }
    }
    &__fields:last-child, &__postal:last-child, &__references:last-child, &__mail:last-child {
      padding-bottom: 100px;
    }
  }
</style>
